import React, { useRef } from "react";
import { Box, Button, Container, Typography, Grid, Avatar, Card, CardContent } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { ArrowDownward } from "@mui/icons-material";
import LanguageToggle from "../components/LanguageToggle";

const cardStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'center',
    p: 3,
    width: '100%',
    maxWidth: '400px',
    borderRadius: 2,
    boxShadow: 3,
    bgcolor: "#f9fafb",
    height: 'auto',
    '&:hover': {
        boxShadow: 6,
        transform: 'scale(1.05)',
        transition: 'all 0.3s ease'
    }
};

const iconStyle = {
    width: 64,
    height: 64,
    marginBottom: 16
};

const LandingPage = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const nextSectionRef = useRef(null);

    const scrollToNextSection = () => {
        nextSectionRef.current?.scrollIntoView({ behavior: "smooth" }); // Scroll to the next section
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6f8", color: "#1a202c" }}>
            {/* Hero Section */}
            <Box
                sx={{
                    minHeight: "100vh",
                    bgcolor: "transparent",
                    color: "#ffffff",
                    background: "linear-gradient(to bottom, rgba(26, 115, 232, 0.8), rgba(26, 115, 232, 0.7)), url('your-image-url.jpg') no-repeat center center",
                    backgroundSize: "cover",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    px: 2
                }}
            >
                {/* Language Toggle */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "20px",
                        right: "20px",
                        zIndex: 10
                    }}
                >
                    <LanguageToggle />
                </Box>

                {/* Main Content Split */}
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 4
                        }}
                    >
                        {/* Left: Text Content */}
                        <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 700,
                                    mb: 3,
                                    fontSize: { xs: "2rem", sm: "2.75rem", md: "3.5rem" },
                                    lineHeight: "1.2"
                                }}
                            >
                                {t("landing.heroTitle")}
                            </Typography>

                            <Typography
                                variant="h6"
                                sx={{
                                    mb: 4,
                                    color: "rgba(224, 224, 224, 0.8)",
                                    fontSize: { xs: "1rem", sm: "1.25rem" }
                                }}
                            >
                                {t("landing.heroDescription")}
                            </Typography>

                            <Button
                                onClick={() => window.location.href = "/login"}
                                variant="contained"
                                sx={{
                                    backgroundColor: "#34d399",
                                    color: "#1a202c",
                                    fontWeight: 600,
                                    px: 6,
                                    py: 2,
                                    fontSize: "1.2rem",
                                    borderRadius: "12px",
                                    textTransform: "none",
                                    '&:hover': {
                                        backgroundColor: "#059669",
                                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)"
                                    }
                                }}
                            >
                                {t("landing.getStartedButton")}
                            </Button>
                        </Box>

                        {/* Right: SVG Illustration */}
                        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                            <img
                                src="/assets/hero.svg"
                                alt="Illustration"
                                style={{ maxWidth: "100%", height: "auto" }}
                            />
                        </Box>
                    </Box>
                </Container>

                {/* Scroll Down Icon */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "30px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        cursor: "pointer",
                        animation: "bounce 2s infinite"
                    }}
                    onClick={scrollToNextSection}
                >
                    <ArrowDownward sx={{ fontSize: 40, color: "#ffffff" }} />
                </Box>
            </Box>

            {/* Features Section */}
            <Box ref={nextSectionRef} sx={{ py: 10, px: 2, bgcolor: "#ffffff" }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 6, textAlign: "center" }}>
                        {t("landing.keyFeaturesTitle")}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            gap: 4,
                        }}
                    >
                        {/* Manage Employees */}
                        <Box sx={cardStyle}>
                            <img src="/assets/manager.svg" alt="Manage Employees" style={iconStyle} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                {t("landing.featureManageEmployeesTitle")}
                            </Typography>
                            <Typography variant="body1">
                                {t("landing.featureManageEmployeesDescription")}
                            </Typography>
                        </Box>

                        {/* Create Projects */}
                        <Box sx={cardStyle}>
                            <img src="/assets/create_projects.svg" alt="Create Projects" style={iconStyle} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                {t("landing.featureCreateProjectsTitle")}
                            </Typography>
                            <Typography variant="body1">
                                {t("landing.featureCreateProjectsDescription")}
                            </Typography>
                        </Box>

                        {/* Assign Tasks */}
                        <Box sx={cardStyle}>
                            <img src="/assets/assign_tasks.svg" alt="Assign Tasks" style={iconStyle} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                {t("landing.featureAssignTasksTitle")}
                            </Typography>
                            <Typography variant="body1">
                                {t("landing.featureAssignTasksDescription")}
                            </Typography>
                        </Box>

                        {/* Log Time */}
                        <Box sx={cardStyle}>
                            <img src="/assets/log_time.svg" alt="Log Time" style={iconStyle} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                {t("landing.featureLogTimeTitle")}
                            </Typography>
                            <Typography variant="body1">
                                {t("landing.featureLogTimeDescription")}
                            </Typography>
                        </Box>

                        {/* Create Templates */}
                        <Box sx={cardStyle}>
                            <img src="/assets/templates.svg" alt="Create Templates" style={iconStyle} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                {t("landing.featureCreateTemplatesTitle")}
                            </Typography>
                            <Typography variant="body1">
                                {t("landing.featureCreateTemplatesDescription")}
                            </Typography>
                        </Box>

                        {/* Phases and Categories */}
                        <Box sx={cardStyle}>
                            <img src="/assets/phases.svg" alt="Phases & Categories" style={iconStyle} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                {t("landing.featurePhasesCategoriesTitle")}
                            </Typography>
                            <Typography variant="body1">
                                {t("landing.featurePhasesCategoriesDescription")}
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Pricing Section */}
            <Box sx={{ py: 10, px: 2, bgcolor: "#edf2f7" }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, textAlign: "center" }}>
                        {t("landing.pricingTitle")}
                    </Typography>

                    {/* Free Trial Text */}
                    <Box sx={{
                        display: 'flex', justifyContent: 'center', width: '100%', mb: 6, textAlign: "center"
                    }}>
                        < Typography variant="h6" sx={{
                            textAlign: "center",
                            color: "#555",
                            width: "700px",
                            fontStyle: "italic",
                            fontSize: { xs: "1rem", sm: "1.2rem" }
                        }}>
                            {t("landing.pricingFreeTrial")}
                        </Typography>
                    </Box>
                    <Box sx={{ mb: 6, textAlign: "center" }}>
                        <Button
                            onClick={() => window.location.href = "/login"}
                            variant="contained"
                            sx={{
                                backgroundColor: "#34d399",
                                color: "#1a202c",
                                fontWeight: 600,
                                px: 6,
                                py: 2,
                                fontSize: "1.2rem",
                                borderRadius: "12px",
                                textTransform: "none",
                                '&:hover': {
                                    backgroundColor: "#059669",
                                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)"
                                }
                            }}
                        >
                            {t("landing.startFreeTrial")}
                        </Button>
                    </Box>

                    {/* Flexbox Container */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "stretch",  // Ensures equal height across all items
                        gap: 4, // Space between cards
                        flexWrap: "wrap" // Allow items to wrap on smaller screens
                    }}>
                        {/* Starter Plan */}
                        <Box sx={{
                            p: 4,
                            bgcolor: "#ffffff",
                            borderRadius: 2,
                            boxShadow: 2,
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",  // Ensures the content is spaced out
                            height: "auto", // Adjusts dynamically to content
                            flex: "1 1 350px", // Flex-grow, flex-shrink, and flex-basis for consistent width and height
                            maxWidth: 350,
                            '&:hover': {
                                boxShadow: 6,
                                transform: 'scale(1.05)',
                                transition: 'all 0.3s ease'
                            }
                        }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {t("landing.pricingStarterTitle")}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, my: 2 }}>$29/mo</Typography>
                            <Typography variant="body1">{t("landing.pricingStarterDescription")}</Typography>
                        </Box>

                        {/* Professional Plan */}
                        <Box sx={{
                            p: 4,
                            bgcolor: "#ffffff",
                            borderRadius: 2,
                            boxShadow: 2,
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            height: "auto", // Auto adjusts based on the content
                            flex: "1 1 350px", // Flexbox properties to make sure the card is the same width as others
                            maxWidth: 350,
                            '&:hover': {
                                boxShadow: 6,
                                transform: 'scale(1.05)',
                                transition: 'all 0.3s ease'
                            }
                        }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {t("landing.pricingProfessionalTitle")}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, my: 2 }}>$79/mo</Typography>
                            <Typography variant="body1">{t("landing.pricingProfessionalDescription")}</Typography>
                        </Box>

                        {/* Enterprise Plan */}
                        <Box sx={{
                            p: 4,
                            bgcolor: "#ffffff",
                            borderRadius: 2,
                            boxShadow: 2,
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            height: "auto", // Auto adjusts based on the content
                            flex: "1 1 350px", // Same width for all cards
                            maxWidth: 350,
                            '&:hover': {
                                boxShadow: 6,
                                transform: 'scale(1.05)',
                                transition: 'all 0.3s ease'
                            }
                        }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {t("landing.pricingEnterpriseTitle")}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, my: 2 }}>Custom</Typography>
                            <Typography variant="body1">{t("landing.pricingEnterpriseDescription")}</Typography>
                        </Box>
                    </Box>
                </Container>
            </Box >

            {/* Team Section */}
            < Box sx={{ py: 10, px: 2, bgcolor: "#f4f6f8" }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 6, textAlign: "center" }}>
                        {t("landing.creatorTitle")}
                    </Typography>

                    <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        maxWidth: "800px",
                        mx: "auto"
                    }}>
                        {/* Avatar or Image */}
                        <Avatar
                            sx={{ width: 120, height: 120, mb: 4, border: "4px solid #34d399" }}
                            src="assets/creator.jpg" // Use your personal image here
                        />

                        {/* Subtitle */}
                        <Typography variant="h6" sx={{ color: "#34d399", fontWeight: 600, mb: 2 }}>
                            {t("landing.creatorSubtitle")}
                        </Typography>

                        {/* Description */}
                        <Typography variant="body1" sx={{ color: "#555", lineHeight: "1.6", mb: 4 }}>
                            {t("landing.creatorDescription")}
                        </Typography>

                        {/* Optional Button or CTA */}
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: "#34d399",
                                color: "#1a202c",
                                fontWeight: 600,
                                px: 6,
                                py: 2,
                                fontSize: "1.2rem",
                                borderRadius: "12px",
                                textTransform: "none",
                                '&:hover': {
                                    backgroundColor: "#059669",
                                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)"
                                }
                            }}
                            onClick={() => window.location.href = "#contact"} // Optional link to contact or further details
                        >
                            {t("landing.contactButton")}
                        </Button>
                    </Box>
                </Container>
            </Box >

            {/* Footer Section */}
            < Box sx={{ bgcolor: "#1a202c", color: "#cbd5e0", py: 4, textAlign: "center" }}>
                <Typography variant="body2">
                    {t("landing.footer")}
                </Typography>
            </Box >
        </Box >
    );
};

export default LandingPage;
